import './AddablePill.css';

import React, {
  useEffect,
  useId,
  useRef,
  useState,
} from 'react';

import DashedPlusButton from '../common/DashedPlusButton/DashedPlusButton';

export interface AddablePillProps {
  items: string[];
  disabled?: boolean;
  onAdd: (label: string) => Promise<void>;
  /** User clicked remove on a pill; parent should confirm then delete. */
  onRemoveItem: (index: number) => void;
  /** Row title above pills. */
  sectionLabel?: string;
  inputPlaceholder?: string;
  addButtonAriaLabel?: string;
  commitButtonAriaLabel?: string;
  removeButtonAriaLabel?: (label: string) => string;
}

const MAX_PILL_LABEL_LEN = 48;

const AddablePill: React.FC<AddablePillProps> = ({
  items,
  disabled = false,
  onAdd,
  onRemoveItem,
  sectionLabel = 'Items',
  inputPlaceholder = 'Label',
  addButtonAriaLabel = 'Add item',
  commitButtonAriaLabel = 'Add item',
  removeButtonAriaLabel = (label) => `Remove ${label}`,
}) => {
  const listInstanceId = useId();
  const [adding, setAdding] = useState(false);
  const [draft, setDraft] = useState('');
  const [saving, setSaving] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (adding && inputRef.current) {
      inputRef.current.focus();
    }
  }, [adding]);

  const handleStartAdd = (): void => {
    if (disabled || saving) {
      return;
    }
    setAdding(true);
    setDraft('');
  };

  const handleCancelAdd = (): void => {
    setAdding(false);
    setDraft('');
  };

  const commitAdd = async (): Promise<void> => {
    const name = draft.trim();
    if (!name || saving) {
      if (!name) {
        handleCancelAdd();
      }
      return;
    }
    setSaving(true);
    try {
      await onAdd(name);
      setDraft('');
      setAdding(false);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="addable-pill">
      <span className="addable-pill-label">{sectionLabel}</span>
      <div className="addable-pill-row">
        {items.map((label, index) => (
          <span
            key={`${listInstanceId}-${index}`}
            className="addable-pill-chip"
          >
            <span className="addable-pill-chip-text">{label}</span>
            <button
              type="button"
              className="addable-pill-chip-remove"
              disabled={disabled || saving}
              onClick={() => onRemoveItem(index)}
              aria-label={removeButtonAriaLabel(label)}
            >
              ×
            </button>
          </span>
        ))}
        {adding ? (
          <span className="addable-pill-chip addable-pill-input-wrap">
            <input
              ref={inputRef}
              className="addable-pill-input"
              value={draft}
              maxLength={MAX_PILL_LABEL_LEN}
              placeholder={inputPlaceholder}
              disabled={saving}
              onChange={(e) => setDraft(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  e.preventDefault();
                  void commitAdd();
                }
                if (e.key === 'Escape') {
                  handleCancelAdd();
                }
              }}
              onBlur={() => {
                if (!draft.trim()) {
                  handleCancelAdd();
                }
              }}
            />
            <button
              type="button"
              className="addable-pill-commit"
              disabled={saving || !draft.trim()}
              onClick={() => void commitAdd()}
              aria-label={commitButtonAriaLabel}
            >
              ✓
            </button>
          </span>
        ) : (
          <DashedPlusButton
            disabled={disabled || saving}
            onClick={handleStartAdd}
            ariaLabel={addButtonAriaLabel}
          />
        )}
      </div>
    </div>
  );
};

export default AddablePill;
